/** Normalizes any thrown value (Error, string, CloseEvent, DOM Event, or a plain object) into a flat record for WebSocket-client structured logging.
 *  Every branch answers the ONE `ExtractedErrorView` shape (`types/websocket-extracted-error.ts`) since 2026-09-02, when this leaf sat at its UTILS_MAX_LINES
 *  utils cap (`QUAL-FILE-SIZE-CAP-HEADROOM-TIGHT-001`): the three per-kind extractors used to re-spell their own subset of it inline. */

import { coerceString } from '@federa/const/string-guards';
import type { ExtractedErrorView } from '@federa/types/websocket-extracted-error';

function isCloseEvent(obj: Record<string, unknown>): boolean {
	return 'code' in obj && 'wasClean' in obj && 'type' in obj;
}

function isDomEvent(obj: Record<string, unknown>): boolean {
	return 'type' in obj && 'target' in obj;
}

function extractCloseEvent(event: Record<string, unknown>): ExtractedErrorView {
	return {
		type: 'CloseEvent',
		message: coerceString(event.reason) || `Connection closed with code ${String(event.code)}`,
		code: String(event.code),
		reason: event.reason,
		wasClean: event.wasClean,
		eventType: event.type,
	};
}

function extractDomEvent(event: Record<string, unknown>): ExtractedErrorView {
	const eventType = event.type;
	let targetInfo = '';
	const target = event.target;
	if (target && typeof target === 'object' && 'url' in target) {
		targetInfo = ` (${(target as { url: string }).url})`;
	}
	return {
		type: 'Event',
		message: `${String(eventType)} event${targetInfo}`,
		eventType,
	};
}

function extractMessageObject(obj: Record<string, unknown>): ExtractedErrorView {
	return {
		type: 'object',
		message: String(obj.message),
		...(obj.code !== undefined && { code: String(obj.code) }),
	};
}

/** The UNKNOWN reading: a value with no branch of its own, carried as its string form rather than dropped. The two exits that reach it -- a non-object primitive, and an object carrying no `message` -- must answer identically, or the same value would read one way as a number and another as a record. */
const unknownErrorView = (error: unknown): ExtractedErrorView => ({ type: 'unknown', message: String(error) });

export function extractError(error: unknown): ExtractedErrorView {
	if (error === null || error === undefined) return { type: 'unknown', message: 'Unknown error (null or undefined)' };
	if (error instanceof Error) {
		return {
			type: 'Error',
			message: error.message,
			stack: error.stack,
			...(error.name !== 'Error' && { code: error.name }),
		};
	}
	if (typeof error === 'string') {
		return { type: 'string', message: error };
	}
	if (typeof error !== 'object') return unknownErrorView(error);
	if (isCloseEvent(error as Record<string, unknown>)) return extractCloseEvent(error as Record<string, unknown>);
	if (isDomEvent(error as Record<string, unknown>)) return extractDomEvent(error as Record<string, unknown>);
	if ('message' in error) return extractMessageObject(error);
	return unknownErrorView(error);
}
